import { useLingui } from "@lingui/react/macro";
import { Button, Input, Spinner } from "@rakazo/ui-web";
import { ArrowRight, Save } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { createSpaceRpc, selectedSpaceId } from "../lib/rpc";
import { errorText } from "../lib/user-error";

/** Navigation and capture use the same owned browser shown in Screen. */
export function ComputerBrowserBar({ botId, hasControl }: { botId: string; hasControl: boolean }) {
  const { t } = useLingui();
  const [client] = useState(() => createSpaceRpc(selectedSpaceId()));
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState<string | null>(null);
  const generation = useRef(0);
  useEffect(() => {
    generation.current++;
    setBusy(false);
    setError("");
    return () => {
      generation.current++;
    };
  }, [botId, hasControl]);
  async function operate(capture: boolean) {
    if (!hasControl || busy) return;
    const current = generation.current;
    setBusy(true);
    setError("");
    setSaved(null);
    try {
      const page = await client.computer.browserPage({ botId, ...(!capture ? { url } : {}) });
      if (current !== generation.current) return;
      setUrl(page.url);
      if (capture) {
        if (page.loading)
          throw new Error(t`The page is still loading. Try capture again when it finishes.`);
        const artifact = await client.artifacts.create({
          botId,
          ...page.capture,
          mimeType: "text/markdown",
          clientNonce: crypto.randomUUID(),
        });
        if (current === generation.current) setSaved(artifact.id);
      }
    } catch (e) {
      if (current === generation.current) setError(errorText(e));
    } finally {
      if (current === generation.current) setBusy(false);
    }
  }
  if (!hasControl) return null;
  return (
    <div className="shrink-0 px-3 pb-2">
      <form
        className="flex min-w-0 items-center gap-1.5"
        onSubmit={(e) => {
          e.preventDefault();
          void operate(false);
        }}
      >
        <Input
          type="url"
          aria-label={t`Browser address`}
          placeholder="https://"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          className="h-8 min-w-0 flex-1 text-xs"
          disabled={busy}
        />
        <Button
          type="submit"
          size="icon-sm"
          variant="ghost"
          aria-label={t`Open address`}
          disabled={busy || !url.trim()}
        >
          {busy ? <Spinner /> : <ArrowRight size={15} />}
        </Button>
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label={t`Capture page to Files`}
          disabled={busy}
          onClick={() => void operate(true)}
        >
          <Save size={15} />
        </Button>
      </form>
      {error && (
        <p role="alert" className="pt-1.5 text-xs text-destructive">
          {error}
        </p>
      )}
      {saved && (
        <Link
          className="inline-block pt-1.5 text-xs underline"
          to={`/app/artifacts/${encodeURIComponent(saved)}?version=${encodeURIComponent(saved)}`}
        >{t`Open in Files`}</Link>
      )}
    </div>
  );
}
